import React from 'react';
import PropTypes from 'prop-types';
import { faSave, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';

import AdminPageContainer from '../common/AdminPageContainer';

import Loader from '../../common/loader/Loader';
import Alert from '../../common/Alert';

import DefaultInput from '../common/DefaultInput';
import EditCourseBlock from './edit/EditCourseBlock';

import { IconButton, BackLink } from '../common/Buttons';

import useEditCoursePageState from './edit/useEditCoursePageState';

const EditCoursePage = props => {
  const {
    isEditMode,
    isLoading,
    isError,
    courseData,
    changeStringValue,
    changeNumberValue,
    changeBoolValue,
    addSection,
    removeSection,
    changeSectionName,
    addWebinar,
    removeWebinar,
    addWebinarParagraph,
    removeWebinarParagraph,
    changeWebinarName,
    changeWebinarParagraph,
    saveData,
  } = useEditCoursePageState(props.match.params.id, props.history);

  return (
    <AdminPageContainer>
      <Loader isVisible={isLoading} />
      {isError && <Alert isError message="An error occured" />}
      <div className="is-flex">
        <BackLink to="/admin/courses" />
        <IconButton className="button is-link ml-20px" text="Save" icon={faSave} onClick={saveData} />
      </div>

      <p className="mt-20px admin-course-title">{isEditMode ? 'Edit Course' : 'Add New Course'}</p>

      <div className="mt-20px">
        <EditCourseBlock title="General course info" isOpen={true}>
          <DefaultInput placeholder="Course Name" value={courseData.name} onChange={changeStringValue('name')} />

          <div className="field">
            <div className="control">
              <textarea
                className="textarea"
                placeholder="Course Description"
                value={courseData.description}
                onChange={changeStringValue('description')}
              ></textarea>
            </div>
          </div>

          <div className="field">
            <label className="checkbox">
              <input type="checkbox" checked={courseData.isDraft} onChange={changeBoolValue('isDraft')} />
              <span className="ml-10px">Is Draft</span>
              <span className="ml-10px admin-text-sm">(If checked, course will not display in courses list)</span>
            </label>
          </div>

          <div className="field">
            <label className="checkbox">
              <input type="checkbox" checked={courseData.isPopular} onChange={changeBoolValue('isPopular')} />
              <span className="ml-10px">Is Popular</span>
              <span className="ml-10px admin-text-sm">
                (If checked, course will display on landing page in carousel block)
              </span>
            </label>
          </div>

          <div className="field">
            <div className="select">
              <select value={courseData.type} onChange={changeNumberValue('type')}>
                <option value={1}>C #</option>
                <option value={2}>Javascript</option>
                <option value={3}>Typescript</option>
                <option value={4}>Nodejs</option>
              </select>
            </div>
          </div>
          <div className="field">
            <div className="select">
              <select value={courseData.level} onChange={changeNumberValue('level')}>
                <option value={1}>Beginner</option>
                <option value={2}>Intermediate</option>
                <option value={3}>Advanced</option>
              </select>
            </div>
          </div>

          <DefaultInput
            label="Course duration"
            value={courseData.duration}
            onChange={changeNumberValue('duration')}
            className="fw-200px"
          />
          <DefaultInput
            label="Number of tasks"
            value={courseData.tasksCount}
            onChange={changeNumberValue('tasksCount')}
            className="fw-200px"
          />
          <DefaultInput
            label="Old price"
            value={courseData.oldPrice}
            onChange={changeNumberValue('oldPrice')}
            className="fw-200px"
          />
          <DefaultInput
            label="Price"
            value={courseData.price}
            onChange={changeNumberValue('price')}
            className="fw-200px"
          />
        </EditCourseBlock>

        <EditCourseBlock title="Sections" isOpen>
          <IconButton className="button is-link" text="New Section" icon={faPlus} onClick={addSection} />

          <div className="mt-20px ml-10px">
            {courseData.sections.map((section, sectionIndex) => (
              <EditCourseBlock
                title={`Section ${sectionIndex + 1}`}
                key={sectionIndex}
                deleteAction={removeSection(sectionIndex)}
              >
                <DefaultInput label="Section name" value={section.name} onChange={changeSectionName(sectionIndex)} />

                <div className="mt-20px ml-20px">
                  <IconButton
                    className="button is-link"
                    text="New Webinar"
                    icon={faPlus}
                    onClick={addWebinar(sectionIndex)}
                  />
                  {section.webinars.map((webinar, webinarIndex) => (
                    <EditCourseBlock
                      key={webinarIndex}
                      className="mt-20px"
                      title={`Webinar ${webinarIndex + 1}`}
                      deleteAction={removeWebinar(sectionIndex, webinarIndex)}
                    >
                      <DefaultInput
                        label="Webinar name"
                        value={webinar.name}
                        onChange={changeWebinarName(sectionIndex, webinarIndex)}
                      />

                      <div className="mt-20px ml-40px">
                        <IconButton
                          className="button is-link"
                          text="New Paragraph"
                          icon={faPlus}
                          onClick={addWebinarParagraph(sectionIndex, webinarIndex)}
                        />
                        {webinar.paragraphs.map((paragraph, paragraphIndex) => (
                          <div className="mt-20px is-flex" key={paragraphIndex}>
                            <DefaultInput
                              placeholder="Paragraph"
                              value={paragraph}
                              onChange={changeWebinarParagraph(sectionIndex, webinarIndex, paragraphIndex)}
                            />
                            <IconButton
                              className="button is-danger ml-20px"
                              icon={faTrash}
                              title="Delete paragraph"
                              onClick={removeWebinarParagraph(sectionIndex, webinarIndex, paragraphIndex)}
                            />
                          </div>
                        ))}
                      </div>
                    </EditCourseBlock>
                  ))}
                </div>
              </EditCourseBlock>
            ))}
          </div>
        </EditCourseBlock>

        <IconButton className="button is-link ml-20px mb-20px" text="Save" icon={faSave} onClick={saveData} />
      </div>
    </AdminPageContainer>
  );
};

EditCoursePage.propTypes = {
  history: PropTypes.object.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
};

export default EditCoursePage;
